import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Package, ArrowRight, Wallet } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/lib/format";
import useSEO from "@/hooks/useSEO";

export default function OrderConfirmation() {
  const { orderId } = useParams();
  const [params] = useSearchParams();
  const [order, setOrder] = useState(null);
  const [payFailed, setPayFailed] = useState(null);
  useSEO({ title: "Order Confirmed — Just Kords", description: "Your Just Kords order is confirmed." });

  useEffect(() => {
    const c = params.get("c");
    if (c) {
      axios.get(`${API}/orders/${orderId}?contact=${encodeURIComponent(c)}`)
        .then((r) => setOrder(r.data)).catch(() => {});
    }
  }, [orderId, params]);

  useEffect(() => {
    if (!order || order.payment_status === "paid" || order.payment_method === "cod") return;
    const timer = setInterval(async () => {
      try {
        const { data } = await axios.get(`${API}/payments/razorpay/status/${orderId}`);
        if (data.payment_status === "paid") {
          clearInterval(timer);
          toast.success("Payment received — your order is confirmed!");
          const c = params.get("c");
          if (c) {
            axios.get(`${API}/orders/${orderId}?contact=${encodeURIComponent(c)}`)
              .then((r) => setOrder(r.data)).catch(() => {});
          } else {
            window.location.reload();
          }
        } else if (data.last_payment_status === "failed") {
          clearInterval(timer);
          setPayFailed(data.failure_reason || "Your payment didn't go through — no money was charged");
        }
      } catch { /* keep polling */ }
    }, 5000);
    return () => clearInterval(timer);
  }, [order, orderId, params]);

  return (
    <div className="flex min-h-[80vh] flex-col items-center justify-center bg-alabaster px-4 pt-28 pb-16 text-center lg:pt-32">
      <span className="rounded-full bg-crimson-sub p-5 text-crimson" data-testid="confirmation-icon"><CheckCircle2 size={44} strokeWidth={1.4} /></span>
      <h1 className="mt-6 font-serif text-5xl font-light">Thank you.</h1>
      <p className="mt-3 max-w-md text-sm leading-relaxed text-mutedc">
        Your order is confirmed and a confirmation has been sent to your email and WhatsApp. Track it anytime below.
      </p>
      <div className="mt-8 border border-linec bg-white px-8 py-6" data-testid="confirmation-order-id">
        <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-mutedc">Order ID</p>
        <p className="mt-1 font-mono text-2xl font-medium text-crimson">{orderId}</p>
      </div>

      {payFailed && (
        <div className="mt-8 w-full max-w-md bg-crimson-sub px-6 py-5 text-left" data-testid="confirmation-failed-banner">
          <p className="text-sm font-semibold text-crimson">Payment not received for this order yet.</p>
          <p className="mt-2 text-xs leading-relaxed text-mutedc">{payFailed}. If money left your account, it will auto-refund in 5-7 working days. You can place the order again — or message us on WhatsApp and we'll sort it out.</p>
        </div>
      )}

      {order && order.cashback_earned_inr > 0 && (
        <div className="mt-8 flex w-full max-w-md items-center gap-4 border border-crimson bg-crimson-sub p-5 text-left" data-testid="confirmation-cashback">
          <Wallet size={22} className="shrink-0 text-crimson" />
          <p className="text-sm leading-relaxed text-ink">
            <span className="font-semibold text-crimson">₹{Number(order.cashback_earned_inr).toLocaleString("en-IN")} cashback</span> has been added to your Just Kords wallet — valid for 90 days. It will be waiting on your next checkout.
          </p>
        </div>
      )}

      {order && (
        <div className="mt-8 w-full max-w-md border border-linec bg-white p-6 text-left" data-testid="confirmation-summary">
          <div className="flex items-center gap-2 text-sm font-semibold"><Package size={16} className="text-crimson" /> {order.items.length} item{order.items.length > 1 ? "s" : ""} · {order.status}</div>
          <div className="mt-4 space-y-2 text-sm">
            {order.items.map((i) => (
              <div key={`${i.slug}:${i.size}`} className="flex justify-between text-mutedc">
                <span>{i.name} × {i.qty} <span className="font-mono text-[10px] uppercase">(S {i.size})</span></span>
                <span>₹{Number(i.price_inr * i.qty).toLocaleString("en-IN")}</span>
              </div>
            ))}
            <div className="flex justify-between border-t border-linec pt-2 font-semibold text-ink">
              <span>Total</span><span>₹{Number(order.total_inr).toLocaleString("en-IN")}</span>
            </div>
          </div>
        </div>
      )}

      <div className="mt-10 flex flex-wrap justify-center gap-4">
        <Link to={`/track-order?id=${orderId}`} data-testid="confirmation-track-link" className="group inline-flex items-center gap-2 rounded-full bg-crimson px-8 py-4 text-sm font-semibold text-white transition-colors hover:bg-crimson-dark">
          Track this order <ArrowRight size={15} className="transition-transform group-hover:translate-x-1" />
        </Link>
        <Link to="/shop" data-testid="confirmation-shop-link" className="rounded-full border border-ink px-8 py-4 text-sm font-semibold transition-colors hover:bg-ink hover:text-white">
          Keep Shopping
        </Link>
      </div>
    </div>
  );
}
